/**
 * api.ts — hooks cho chợ nhận đơn (ekip tự nhận đơn, spec 16/08/2026).
 *
 * Endpoints:
 *   GET  /ekip/pool?date=
 *   POST /ekip/claims/:orderId/:slot            (giơ tay)
 *   POST /ekip/claims/:orderId/:slot/withdraw   (tự rút)
 *   GET  /ekip/claims/disputes                  (leader)
 *   POST /ekip/claims/:orderId/:slot/resolve    (leader chọn người thắng)
 *   POST /ekip/claims/lock-month                (khoá tháng)
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage, serverRawMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { useAuthV2 } from "@/lib/authv2/store";

export type EkipPoolItem = components["schemas"]["EkipPoolItem"];
export type EkipPoolSlot = components["schemas"]["EkipPoolSlot"];
export type EkipClaim = components["schemas"]["EkipClaim"];
export type EkipLeaderView = components["schemas"]["EkipLeaderView"];
export type EkipDisputeGroup = components["schemas"]["EkipDisputeGroup"];
export type EkipStaleSlot = components["schemas"]["EkipStaleSlot"];
export type ClaimSlotName = "photo" | "makeup";

export const ekipPoolKeys = {
  // `brand` NẰM TRONG khoá: lọc thương hiệu là một tập đơn khác, dùng chung
  // khoá thì chọn xong vẫn thấy danh sách cũ cho tới lần nạp sau — đúng kiểu
  // "lọc không ăn thua" mà không ai tìm ra vì mạng vẫn trả 200.
  pool: (date: string, brand = "") => ["ekip", "pool", date, brand] as const,
  disputes: () => ["ekip", "disputes"] as const,
  mine: () => ["ekip", "claims", "mine"] as const,
};

/** Chỉ bộ phận chụp/makeup mới có suất để giơ tay. */
const CLAIM_DEPT = /^(photo|makeup)/;

/**
 * Lỗi 409/403 của claim mang mã ổn định — dịch sang câu tiếng Việt dễ hiểu.
 * Câu không có trong danh sách → undefined: readError chuyển nó cho bộ dịch
 * chung, KHÔNG đổi thành một câu chung chung (CEO chốt 24/09/2026 — từng giấu
 * "đơn này do máy chia việc quản…" và "only leaders can assign claim slots").
 */
export function claimErrorMessage(raw: string): string | undefined {
  // Chứng chỉ chuyên ngành (QĐ 0101): máy chủ đã nói tiếng Việt kèm nhãn
  // chuyên ngành ("cần chứng chỉ Ảnh thẻ — hỏi trưởng cửa hàng", "gán ngoại lệ
  // cần lý do…") — dịch lại là mất nhãn.
  if (raw.includes("chứng chỉ") || raw.includes("ngoại lệ")) return raw;
  if (raw.includes("shoot start")) return "Chưa tới giờ chụp — suất mở nhận từ giờ bắt đầu.";
  if (raw.includes("assigned by leader")) return "Suất này leader đã gán người rồi.";
  if (raw.includes("locked for payroll")) return "Tháng này đã khoá để tính lương.";
  if (raw.includes("another department")) return "Suất này thuộc bộ phận khác.";
  if (raw.includes("another branch")) return "Đơn thuộc chi nhánh khác.";
  if (raw.includes("cancelled")) return "Đơn đã hủy.";
  if (raw.includes("no active claim")) return "Người được chọn không còn giữ yêu cầu.";
  // Gán tay: BE trả nguyên văn tiếng Việt cho 3 lỗi chọn sai người.
  if (raw.includes("bộ phận của suất")) return "Người được chọn không thuộc bộ phận của suất.";
  if (raw.includes("chi nhánh của đơn")) return "Người được chọn không làm ở chi nhánh của đơn.";
  if (raw.includes("staff is not active")) return "Nhân sự này đã nghỉ.";
  return undefined;
}

/**
 * readError — the BE wraps every failure in a NESTED envelope
 * `{"error":{"code","message"}}` (platform/errs/handler.go). Reading a flat
 * `error.message` always came back empty, so every 409/403 fell through to the
 * generic fallback and the explanations above never reached the user.
 *
 * A refusal the claim list does not know goes to the shared translator: a
 * Vietnamese reason is shown verbatim, an English one is translated (by the
 * table, then its error code), otherwise the button's own `fallback`.
 */
export function readError(error: unknown, fallback: string): string {
  const raw = serverRawMessage(error);
  return (raw && claimErrorMessage(raw)) || serverErrorMessage(error, fallback);
}

export function useEkipPool(date: string, brand = "") {
  return useQuery({
    queryKey: ekipPoolKeys.pool(date, brand),
    queryFn: async () => {
      // Khoá `brand` chỉ CÓ MẶT khi người dùng chọn: `exactOptionalPropertyTypes`
      // phân biệt "bỏ khoá" với "khoá mang giá trị undefined", và máy chủ đọc
      // chuỗi rỗng là không lọc.
      const query = brand ? { date, brand } : { date };
      const { data, error } = await api.GET("/ekip/pool", {
        params: { query },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách đơn chờ nhận"));
      return data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Mã các đơn mình đang giơ tay. Khi suất tranh chấp chỉ người giơ tay đầu tiên
 * đứng tên trên đơn, nhưng cả nhóm đều làm ca đó (CEO chốt 16/08/2026) — nên
 * màn lịch/đơn hàng mở quyền nhập link ảnh, ghi chú theo danh sách này.
 */
export function useMyClaimedOrderIds(): Set<string> {
  const dept = useAuthV2((s) => s.currentStaff?.department ?? "");
  const { data } = useQuery({
    queryKey: ekipPoolKeys.mine(),
    queryFn: async () => {
      const { data, error } = await api.GET("/ekip/claims/mine", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được suất đang nhận"));
      return data;
    },
    enabled: CLAIM_DEPT.test(dept.toLowerCase()),
    staleTime: 30_000,
  });
  return useMemo(() => new Set((data?.items ?? []).map((c) => c.orderId)), [data]);
}

type SlotTarget = { orderId: string; slot: ClaimSlotName };

function useSlotMutation(
  path:
    | "/ekip/claims/{orderId}/{slot}"
    | "/ekip/claims/{orderId}/{slot}/withdraw",
  fallback: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, slot }: SlotTarget) => {
      const { data, error } = await api.POST(path, {
        params: { path: { orderId, slot } },
      });
      if (error || !data) throw new Error(readError(error, fallback));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ekip", "pool"] });
      void qc.invalidateQueries({ queryKey: ekipPoolKeys.disputes() });
      void qc.invalidateQueries({ queryKey: ekipPoolKeys.mine() });
      // Nhận/rút suất là đổi ekip ngay trên đơn → lịch chung và đơn hàng phải
      // tải lại thì mới thấy tên người nhận.
      void qc.invalidateQueries({ queryKey: ["ekip", "schedule"] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

/** Giơ tay nhận suất. */
export function useClaimSlot() {
  return useSlotMutation("/ekip/claims/{orderId}/{slot}", "Nhận đơn thất bại");
}

/** Tự rút yêu cầu của mình. */
export function useWithdrawClaim() {
  return useSlotMutation(
    "/ekip/claims/{orderId}/{slot}/withdraw",
    "Rút yêu cầu thất bại",
  );
}

/** Màn leader: tranh chấp + đơn ế + số tổng. */
export function useEkipDisputes(enabled: boolean) {
  return useQuery({
    queryKey: ekipPoolKeys.disputes(),
    queryFn: async () => {
      const { data, error } = await api.GET("/ekip/claims/disputes", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách tranh chấp"));
      return data;
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** Sau khi suất đổi chủ: chợ đơn, màn phân xử, lịch chung và đơn hàng đều lệch. */
function invalidateSlotViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["ekip", "pool"] });
  void qc.invalidateQueries({ queryKey: ekipPoolKeys.disputes() });
  void qc.invalidateQueries({ queryKey: ekipPoolKeys.mine() });
  void qc.invalidateQueries({ queryKey: ["ekip", "schedule"] });
  void qc.invalidateQueries({ queryKey: ["orders"] });
}

/** Leader chọn người thắng tranh chấp. */
export function useResolveDispute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, slot, staffId }: SlotTarget & { staffId: string }) => {
      const { data, error } = await api.POST(
        "/ekip/claims/{orderId}/{slot}/resolve",
        { params: { path: { orderId, slot } }, body: { staffId } },
      );
      if (error || !data) throw new Error(readError(error, "Phân xử thất bại"));
      return data;
    },
    onSuccess: () => invalidateSlotViews(qc),
  });
}

/**
 * Gán tay một người vào suất ế — không cần ai giơ tay trước.
 * BE đóng luôn mọi claim đang sống trên suất, nên tranh chấp (nếu có) tan theo.
 */
export function useAssignSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      slot,
      staffId,
      reason,
    }: SlotTarget & { staffId: string; reason?: string }) => {
      const { data, error } = await api.POST("/ekip/claims/{orderId}/{slot}/assign", {
        params: { path: { orderId, slot } },
        // `reason` = lý do gán ngoại lệ chứng chỉ (QĐ 0101), chỉ gửi khi có.
        body: { staffId, ...(reason ? { reason } : {}) },
      });
      if (error || !data) throw new Error(readError(error, "Gán suất thất bại"));
      return data;
    },
    onSuccess: () => invalidateSlotViews(qc),
  });
}

/** Khoá tháng để chốt lương — mọi suất tạm quy thành chốt hẳn. */
export function useLockMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (month: string) => {
      const { data, error } = await api.POST("/ekip/claims/lock-month", {
        body: { month },
      });
      if (error || !data) throw new Error(readError(error, "Khoá tháng thất bại"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ekip", "pool"] });
      void qc.invalidateQueries({ queryKey: ekipPoolKeys.disputes() });
    },
  });
}
